import { apiUrl } from "../utils/api";
import type { FirmenRolle } from "../utils/firmenGesellschafterArt";

/**
 * Firmendaten aus Impressumsangaben — für die Anlagemaske („Daten befüllen")
 * und den Bereinigungsbereich.
 */

/** Feldnamen wie im Firmen-Formular. `postalcode` ist dort klein geschrieben. */
export interface BefundFelder {
  /**
   * Der Rechtsträger laut Impressum — als „Firmenname" übernehmbar. Das ist das
   * einzige Feld, das einen bereits ausgefüllten Wert ERSETZEN darf: getippt
   * wird meist die Kurzform („Muster Bau"), im Impressum steht der vollständige
   * Name („Muster Bau GmbH & Co. KG").
   */
  name: string | null;
  website: string | null;
  phone: string | null;
  fax: string | null;
  email: string | null;
  handelsregisterNummer: string | null;
  registergericht: string | null;
  umsatzsteuernummer: string | null;
  steuernummer: string | null;
  street: string | null;
  postalcode: string | null;
  city: string | null;
  rechtsform: string | null;
  vertretung: string | null;
}

export interface BefundDaten {
  status: string;
  /** Der im Impressum genannte Rechtsträger — zum Vergleich mit dem Eingetippten. */
  firmenbezeichnung: string | null;
  impressumUrl: string | null;
  nurStartseite: boolean;
  score: number;
  abgelehnt: boolean;
  begruendung: string[];
  warnungen: string[];
  /** Alle gefundenen E-Mail-Adressen — `felder.email` ist nur die erste davon. */
  mails?: string[];
  /** Alle gefundenen Rufnummern — `felder.phone` ist nur die erste davon. */
  telefone?: string[];
  felder: BefundFelder;
}

export interface SuchKandidat {
  url: string;
  domain: string;
  titel: string;
  snippet: string;
  gefundenVia: Array<{ strategie: string; rang: number }>;
  geprueft: boolean;
  /** Rechtsträger laut Impressum. Die entscheidende Angabe für die Auswahl. */
  firmenbezeichnung: string | null;
  score: number;
  begruendung: string[];
  warnungen: string[];
  ablehnungsgrund: string | null;
  impressumUrl: string | null;
}

export interface AnlageErgebnis {
  modus: "domain" | "suche";
  domain: string | null;
  befund: BefundDaten | null;
  kandidaten: SuchKandidat[];
  anfragen: Array<{ strategie: string; anfrage: string; treffer: number; fehler?: string }>;
  hinweise: string[];
}

export interface AnlageEingabe {
  name: string;
  ort?: string | null;
  plz?: string | null;
  strasse?: string | null;
  website?: string | null;
  email?: string | null;
}

/** POST mit JSON-Body — bewusst rohes fetch: apiFetch setzt keinen Content-Type. */
const post = async <T>(path: string, body?: unknown): Promise<T> => {
  const res = await fetch(apiUrl(path), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });
  if (!res.ok) {
    let msg = `Aktion fehlgeschlagen: HTTP ${res.status}`;
    try {
      const d = await res.json();
      if (d?.message)
        msg = Array.isArray(d.message) ? d.message.join(", ") : String(d.message);
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return res.json() as Promise<T>;
};

/**
 * Lesender Aufruf. Eigener Helfer statt eines nackten `fetch`, damit die
 * Fehlermeldung des Servers auch hier ankommt — ein bloßes „HTTP 403" ist für
 * den Anwender nichts wert.
 */
const get = async <T>(path: string): Promise<T> => {
  const res = await fetch(apiUrl(path));
  if (!res.ok) {
    let msg = `Aktion fehlgeschlagen: HTTP ${res.status}`;
    try {
      const d = await res.json();
      if (d?.message)
        msg = Array.isArray(d.message) ? d.message.join(", ") : String(d.message);
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return res.json() as Promise<T>;
};

/**
 * Daten für eine noch nicht angelegte Firma ermitteln. Steht eine Website oder
 * Firmen-E-Mail in der Maske, wird deren Impressum direkt gelesen (kostet keine
 * Suchanfrage); sonst wird über den Namen gesucht.
 */
export const firmendatenFuerAnlage = (eingabe: AnlageEingabe & { nurSuche?: boolean }) =>
  post<AnlageErgebnis>("firmendaten/suche/anlage", eingabe);

/**
 * Zweiter Schritt: die gefundenen Adressen laden und ihre Impressen bewerten.
 * Getrennt von der Suche, damit die Trefferliste sofort erscheint — das Laden von
 * fünf Websites dauert mehrere Sekunden, die Suche nur eine halbe. Kostet keine
 * neuen Suchanfragen.
 */
export const firmendatenPruefeKandidaten = (
  urls: string[],
  eingabe: { name: string; ort?: string | null; plz?: string | null },
) =>
  post<{ kandidaten: SuchKandidat[]; hinweise: string[] }>(
    "firmendaten/suche/anlage/pruefen",
    { urls, ...eingabe },
  );

/** Eine ausgewählte Kandidaten-Seite auswerten. */
export const firmendatenBefundFuerUrl = (
  url: string,
  eingabe: { name: string; ort?: string | null; plz?: string | null },
) =>
  post<{ domain: string | null; befund: BefundDaten | null; hinweise: string[] }>(
    "firmendaten/suche/anlage/befund",
    { url, ...eingabe },
  );

/** Ergebnis des Registerabrufs: das abgelegte Dokument in der Kundenakte. */
export interface RegisterAbdruck {
  documentId: number;
  fileName: string;
  mimeType: string | null;
  /** Zeitpunkt des Portalabrufs — beim Abdruck die entscheidende Angabe. */
  abgerufenAm: string | null;
  /** false = vorhandener Abdruck wiederverwendet, das Portal wurde nicht befragt. */
  neuAbgerufen: boolean;
  /** Trefferzeile des Portals, zur Kontrolle des Anwenders. */
  treffer?: string | null;
}

/**
 * Aktuellen Handelsregister-Abdruck der Firma bereitstellen: das Backend holt
 * ihn beim Registerportal und legt ihn in der Kundenakte ab. Ohne `neu` wird
 * ein bereits abgelegter Abdruck wiederverwendet — das Portal begrenzt die
 * Abrufe je Stunde.
 */
export const registerAbdruckAbrufen = (contactId: number, neu = false) =>
  post<RegisterAbdruck>(`firmendaten/register-abdruck/${contactId}`, { neu });

/** Ein schon abgelegter Abdruck (aktuell oder chronologisch). */
export interface AbgelegterAbdruck {
  documentId: number;
  fileName: string;
  mimeType: string | null;
  abgerufenAm: string | null;
}

/**
 * Was zu einer Firma schon abgerufen wurde — fragt das Portal NICHT.
 * `nummer`/`registergericht` sind der gespeicherte Stand; weicht die Maske
 * davon ab, gilt „erst speichern" (die Abrufe lesen aus der Datenbank).
 */
export interface RegisterStand {
  nummer: string | null;
  registergericht: string | null;
  aktuell: AbgelegterAbdruck | null;
  chronologisch: AbgelegterAbdruck | null;
}

export const registerStandLaden = (contactId: number) =>
  get<RegisterStand>(`firmendaten/register-stand/${contactId}`);

/** Ein Beteiligter aus dem Registerinhalt, abgeglichen mit dem Kontaktbestand. */
export interface RegisterBeteiligter {
  rolleCode: string | null;
  /** KMVS-Bezeichnung — wird zum Beziehungstyp („Geschäftsführer"). */
  rolle: string;
  /** Wortlaut des Registers („Geschäftsführer(in)"). */
  rolleAmtlich: string | null;
  leitung: boolean;
  vertretungsbefugnis: string | null;
  art: "person" | "organisation";
  vorname?: string | null;
  nachname?: string | null;
  geburtsdatum?: string | null;
  bezeichnung?: string | null;
  /** Wohn-/Sitzort. Nur zur Kontrolle — wird nicht als Adresse übernommen. */
  ort?: string | null;
  /** So wird verbunden: Beziehungstyp („Mensch fürs Unternehmen") … */
  beziehungstyp: string;
  /** … und Funktion darin („Vorstand", „Prokurist"). */
  beziehungsfunktion?: string;
  /**
   * Zu bestätigen, nicht angewendet: Bei einer GmbH & Co. KG ist eine beteiligte
   * Gesellschaft in aller Regel die Komplementärin und eine Person ein
   * Kommanditist — sicher ist das nicht. Der Prüfen-Schritt zeigt Vorschlag und
   * Begründung; bestätigt wird über `UebernahmeEintrag.funktion`.
   */
  rollenVorschlag?: { funktion: string; begruendung: string };
  /**
   * Woher der Vorschlag stammt. Der Registerinhalt ist eine amtliche
   * Eintragung; die Gesellschafterliste ein eingereichtes — oft eingescanntes
   * — Dokument, dessen Auswertung geprüft gehört.
   */
  herkunft?: "register" | "gesellschafterliste";
  /** Beteiligungsquote mit Einheit („25,00 %"), nur aus der Gesellschafterliste. */
  anteil?: string | null;
  /**
   * Registerangaben der beteiligten Gesellschaft („HRB 776556" / „Stuttgart").
   * Damit ist sie selbst wieder abrufbar — der Punkt, an dem sich eine
   * Beteiligungskette weiterverfolgen lässt.
   */
  registerNummer?: string | null;
  registergericht?: string | null;
  /** 'vorhanden' = Nachname + Geburtsdatum treffen, 'aehnlich' = nur der Name. */
  abgleich: "neu" | "vorhanden" | "aehnlich";
  kontaktId?: number;
  kontaktName?: string;
  beziehungVorhanden: boolean;
  vorausgewaehlt: boolean;
  /**
   * Komplementär-GmbH einer GmbH & Co. KG — reine Verwaltungsgesellschaft, die
   * nie angelegt wird (T11). Der Dialog zeigt sie grau und nicht wählbar.
   */
  verwaltungsgesellschaft?: boolean;
}

export interface RegisterBeteiligteErgebnis {
  firma: {
    name?: string | null;
    sitz?: string | null;
    rechtsform?: string | null;
    vertretungsregelung?: string | null;
  };
  treffer: string | null;
  beteiligte: RegisterBeteiligter[];
  /** Ehrliche Einschränkungen der Quelle — z. B. „per Texterkennung gelesen". */
  hinweise?: string[];
  /** Das Belegdokument in der Akte, gegen das der Anwender prüft. */
  dokument?: { documentId: number | null; fileName: string | null; mimeType: string | null } | null;
  /**
   * Der aktuelle Abdruck, den derselbe Portalvorgang mitgebracht hat — schon in
   * der Akte abgelegt. `null` in der Anlagemaske (keine Akte) und dann, wenn
   * das Register keinen hergab. Ein zweiter Klick auf „Aktueller Abdruck" ist
   * damit unnötig, und das Stundenkontingent bleibt für andere Firmen übrig.
   */
  abdruck: RegisterAbdruck | null;
}

/** Was beim Übernehmen zurückgeschickt wird — der Server holt nicht neu. */
export interface UebernahmeEintrag {
  rolle: string;
  art: "person" | "organisation";
  vorname?: string | null;
  nachname?: string | null;
  geburtsdatum?: string | null;
  bezeichnung?: string | null;
  vertretungsbefugnis?: string | null;
  /** Anrede — das Register führt keine; sie wird im Dialog gesetzt. */
  anrede?: string | null;
  /** Beteiligungsquote mit Einheit („25,00 %") — wird an der Beziehung gespeichert. */
  anteil?: string | null;
  /** Registerangaben der Gesellschaft — landen in ihren Firmenstammdaten. */
  registerNummer?: string | null;
  registergericht?: string | null;
  /** Die bestätigte Rolle innerhalb des Beziehungstyps. Leer = Ableitung gilt. */
  funktion?: string | null;
  /** Kontaktstatus. Wirkt nur auf Personen, überschreibt nie einen „Kunde". */
  statusName?: string | null;
  kontaktId?: number | null;
  /**
   * Firma als schlichte Gesellschafterin: gewählte Art („Mutterfirma" oder
   * „Beteiligung"). Pflicht — ohne Wahl verbindet der Server nichts
   * (utils/firmenGesellschafterArt.ts).
   */
  firmenRolle?: FirmenRolle | null;
}

export interface UebernahmeErgebnis {
  angelegt: Array<{ kontaktId: number; name: string; rolle: string; neu: boolean }>;
  fehler: Array<{ name: string; grund: string }>;
  /** Bewusst nicht angelegt (Komplementär-GmbH, T11) — kein Fehler. */
  uebersprungen?: Array<{ name: string; grund: string }>;
}

/** Organe und Gesellschafter der Firma aus dem Handelsregister vorschlagen. */
export const registerBeteiligteAbrufen = (contactId: number) =>
  post<RegisterBeteiligteErgebnis>(`firmendaten/register-beteiligte/${contactId}`);

/**
 * Dasselbe für die Anlagemaske: Die Firma gibt es noch nicht, Nummer und
 * Gericht kommen aus dem Formular. Verbunden wird erst nach dem Speichern.
 */
export const registerBeteiligteVorschau = (angaben: {
  nummer: string;
  registergericht: string;
  registerart?: string | null;
}) => post<RegisterBeteiligteErgebnis>("firmendaten/register-beteiligte/vorschau", angaben);

/** Die ausgewählten Beteiligten anlegen und mit der Firma verbinden. */
export const registerBeteiligteUebernehmen = (
  contactId: number,
  eintraege: UebernahmeEintrag[],
) =>
  post<UebernahmeErgebnis>(`firmendaten/register-beteiligte/${contactId}/uebernehmen`, {
    eintraege,
  });

/**
 * Eine Antwort, die noch nicht fertig sein muss.
 *
 * `laeuft` heißt: Das Dokument liegt in der Akte, die Texterkennung ist noch
 * nicht durch. Erneut fragen — mit der `documentId` kostet das keinen weiteren
 * Registerabruf.
 */
interface Nachfassbar {
  status: "fertig" | "laeuft" | "unlesbar";
  documentId: number | null;
}

/**
 * So lange nachfragen, bis die Texterkennung durch ist.
 *
 * Warum nicht einfach warten? Weil zwischen Browser und Backend ein Proxy
 * steht, der nach 30 Sekunden auflegt — und die Texterkennung eines
 * gescannten Vertrags dauert rund 34. Der Aufruf kam also erfolgreich zurück,
 * nur hörte niemand mehr zu, und die Oberfläche meldete Fehlschlag. Jetzt
 * antwortet das Backend sofort und wird hier so lange gefragt, bis es fertig
 * ist.
 */
const NACHFASS_GRENZE_MS = 180_000;

async function bisFertig<T extends Nachfassbar>(
  abrufen: () => Promise<T>,
  nachfassen: (documentId: number) => Promise<T>,
  melden?: (laeuft: boolean) => void,
): Promise<T> {
  let antwort = await abrufen();
  const bis = Date.now() + NACHFASS_GRENZE_MS;

  try {
    while (antwort.status === "laeuft" && antwort.documentId) {
      melden?.(true);
      if (Date.now() >= bis) {
        // Ehrlich abbrechen statt ein leeres Ergebnis auszugeben: Ohne Text
        // sähe die Antwort aus wie „nichts gefunden", obwohl das Dokument da
        // ist und nur noch nicht gelesen wurde.
        throw new Error(
          "Die Texterkennung dauert ungewöhnlich lange. Das Dokument liegt in der " +
            "Kundenakte — bitte gleich noch einmal auswerten.",
        );
      }
      // Jeder Aufruf wartet im Backend selbst einige Sekunden auf das Ergebnis,
      // die Schleife läuft also nicht heiß.
      antwort = await nachfassen(antwort.documentId);
    }
  } finally {
    melden?.(false);
  }
  return antwort;
}

/** Was der Registerordner zur Gesellschafterliste hergegeben hat. */
export interface GesellschafterlisteAntwort extends Nachfassbar {
  /** Die Liste kam aus der Akte — kein Abruf gegen das Stundenkontingent. */
  ausDerAkte: boolean;
  documentId: number | null;
  fileName: string | null;
  mimeType: string | null;
  /** Aufnahmedatum in den Registerordner. */
  stand: string | null;
  /** Datum der Liste selbst („Liste vom 12.03.2021"). */
  listeVom: string | null;
  stammkapital: string | null;
  /** Die Liste lag nur als Scan vor und wurde per Texterkennung gelesen. */
  texterkennung: boolean;
  /**
   * Aus dem Dokument war überhaupt kein Text zu gewinnen. Etwas anderes als
   * „keine Gesellschafter gefunden" — hier hilft nur das Dokument selbst.
   */
  unlesbar: boolean;
  eingereichteListen: string[];
  /** Welche Einreichung genommen wurde — `aelteste` sind die Gründer. */
  waehle: "neueste" | "aelteste";
  vorschlaege: RegisterBeteiligter[];
  hinweise: string[];
}

/**
 * Die Gesellschafter einer GmbH aus der Liste im Registerordner.
 *
 * Eigener Abruf, weil die Gesellschafter einer GmbH nirgends sonst stehen: Der
 * Registerauszug führt nur die Organe. Das Dokument landet in der Kundenakte —
 * es ist der Beleg, gegen den die Vorschläge geprüft werden.
 */
export const gesellschafterlisteAbrufen = (
  contactId: number,
  waehle: "neueste" | "aelteste" = "neueste",
  melden?: (laeuft: boolean) => void,
) =>
  bisFertig<GesellschafterlisteAntwort>(
    () => post(`firmendaten/gesellschafterliste/${contactId}`, { waehle }),
    (documentId) =>
      post(`firmendaten/gesellschafterliste/${contactId}`, { waehle, documentId }),
    melden,
  );

/** Ein Schritt des automatischen Verflechtungsausbaus. */
export interface VerflechtungSchritt {
  kontaktId: number;
  name: string;
  tiefe: number;
  status:
    | "ausgebaut"
    | "ohne-register"
    | "keine-liste"
    | "uebersprungen"
    /** Die Liste liegt in der Akte, die Texterkennung läuft noch. */
    | "wird-gelesen"
    | "fehler";
  verbunden?: number;
  neu?: number;
  /** Wie viele Vorschläge diese Gesellschaft beigesteuert hat. */
  gefunden?: number;
  meldung?: string;
}

/**
 * Ein Fund des Laufs — angelegt ist damit nichts. Der Anwender bearbeitet ihn
 * im Prüfen-Schritt und bestätigt ihn erst danach.
 */
export interface VerflechtungVorschlag {
  /** Stabiler Schlüssel aus dem Inhalt — auch über mehrere Runden hinweg. */
  schluessel: string;
  /** Die Gesellschaft, zu der der Fund gehört — dorthin wird verbunden. */
  firmaId: number;
  firmaName: string;
  tiefe: number;
  /** Aus welchem Portalzweig: Gesellschafterliste oder Registerinhalt. */
  zweig: "gesellschafter" | "organ";
  /** Genau das, was beim Bestätigen zurückgeschickt wird. */
  eintrag: UebernahmeEintrag;
  anzeige: {
    name: string;
    rolle: string;
    rolleAmtlich: string | null;
    ort: string | null;
    abgleich: "neu" | "vorhanden" | "aehnlich";
    kontaktName: string | null;
    beziehungstyp: string;
    beziehungsfunktion: string | null;
    herkunft: "register" | "gesellschafterliste";
    anteil: string | null;
  };
  /** Vorauswahl. `false` bei allem, was die Schutzregeln zurückhalten. */
  vorausgewaehlt: boolean;
  /** Warum nicht vorausgewählt. */
  grund?: string;
}

/** Ergebnis des Bestätigens. */
export interface VerflechtungUebernahme {
  angelegt: Array<{
    firmaId: number;
    kontaktId: number;
    name: string;
    rolle: string;
    neu: boolean;
  }>;
  fehler: Array<{ firmaId: number; name: string; grund: string }>;
}

export interface VerflechtungBericht {
  /** `laeuft`: Der Durchgang arbeitet noch — erneut fragen, ohne neue Kosten. */
  status: "fertig" | "laeuft";
  wurzel: { id: number; name: string };
  tiefe: number;
  schritte: VerflechtungSchritt[];
  /** Was der Lauf gefunden hat. Angelegt ist davon nichts. */
  vorschlaege: VerflechtungVorschlag[];
  /** Wann der Lauf lief (ISO) — für „Vorschläge von 14:32". */
  gesammeltAm: string;
  /** Was beim nächsten Aufruf dran wäre — die Warteschlange wird nicht gespeichert. */
  offen: Array<{ kontaktId: number; name: string; tiefe: number }>;
  kontingentErschoepft: boolean;
  /** Namensgleiche Personen, die bewusst NICHT automatisch angelegt wurden. */
  zurPruefung: Array<{ name: string; firma: string; grund: string }>;
  vorgaenge: number;
}

/**
 * Die Beteiligungskette der Firma ausbauen — der Lauf SAMMELT nur.
 *
 * Begrenzt und wiederaufsetzbar: Ein Aufruf verbraucht nur ein paar
 * Portalvorgänge und meldet den Rest als `offen`. Erneut aufrufen macht dort
 * weiter — die Warteschlange wird jedes Mal aus den Daten abgeleitet.
 * Angelegt wird nichts; dafür gibt es `verflechtungUebernehmen`.
 */
export const verflechtungAusbauen = (
  contactId: number,
  opts: {
    tiefe?: number;
    maxVorgaenge?: number;
    mitOrganen?: boolean;
    /** Einen noch offenen Zwischenstand wegwerfen und frisch beginnen. */
    verwerfen?: boolean;
  } = {},
) => post<VerflechtungBericht>(`firmendaten/verflechtung/${contactId}`, opts);

/**
 * Der letzte, noch nicht bestätigte Lauf.
 *
 * Ein Lauf bezahlt seine Portalvorgänge sofort. Nach einem Neuladen der Seite
 * holt der Dialog den Stand hier ab, statt alles noch einmal abzurufen.
 */
export const verflechtungStand = (contactId: number) =>
  get<{ bericht: VerflechtungBericht | null }>(`firmendaten/verflechtung/${contactId}/stand`);

/** Die bestätigten Vorschläge anlegen und verbinden — je Gesellschaft gebündelt. */
export const verflechtungUebernehmen = (
  contactId: number,
  gruppen: Array<{ firmaId: number; eintraege: UebernahmeEintrag[] }>,
) =>
  post<VerflechtungUebernahme>(`firmendaten/verflechtung/${contactId}/uebernehmen`, {
    gruppen,
  });

/** Ein Feld der Firmenmaske, das aus dem Gesellschaftsvertrag befüllt werden kann. */
export interface Feldvorschlag {
  /** Feldname am Kontakt — die Maske übersetzt ihn in ihren eigenen. */
  feld:
    | "jobOccupation"
    | "financialYearEnd"
    | "unternehmensfunktion"
    | "gruendungsdatum"
    | "vorsteuerAbzugsberechtigt"
    | "bilanzierungspflichtig"
    | "pruefungspflichtHgb"
    /** Rechtsvorgänger (frühere Rechtsform): `wert` ist die Katalog-Id. */
    | "oldLegalForm";
  bezeichnung: string;
  wert: string;
  /** Klartext zu `wert`, wo der Wert eine Id ist (Rechtsform-Katalog). */
  anzeige?: string;
  /** `ergaenzen` hängt an, was schon dasteht, statt es zu ersetzen. */
  modus: "ersetzen" | "ergaenzen";
  vorhanden: string | null;
  begruendung?: string;
}

export interface VertragsAntwort extends Nachfassbar {
  documentId: number | null;
  fileName: string | null;
  mimeType: string | null;
  stand: string | null;
  texterkennung: boolean;
  unlesbar: boolean;
  /** `null`, solange die Texterkennung läuft. */
  befund: {
    firma: string | null;
    sitz: string | null;
    gegenstand: string | null;
    geschaeftsjahrText: string | null;
    geschaeftsjahrBeginn: string | null;
    stammkapital: string | null;
  } | null;
  verbundrolle: { funktionen: string[]; begruendung: string[]; hinweise: string[] } | null;
  vorschlaege: Feldvorschlag[];
  hinweise: string[];
}

/**
 * Den Gesellschaftsvertrag aus dem Registerordner auswerten.
 *
 * Er ist die einzige Quelle im Register für den Unternehmensgegenstand — und
 * damit für Tätigkeitsbeschreibung, Geschäftsjahr und die Rolle im Verbund.
 */
export const gesellschaftsvertragAuswerten = (
  contactId: number,
  melden?: (laeuft: boolean) => void,
) =>
  bisFertig<VertragsAntwort>(
    () => post(`firmendaten/gesellschaftsvertrag/${contactId}`),
    (documentId) => post(`firmendaten/gesellschaftsvertrag/${contactId}`, { documentId }),
    melden,
  );

/** Eine Eintragung im Register, wie der chronologische Abdruck sie führt. */
export interface Historieneintrag {
  nummer: string | null;
  datum: string | null;
  text: string;
  /** Formwechsel, Verschmelzung, Spaltung — verändert den Rechtsträger selbst. */
  rechtsnachfolge: boolean;
}

export interface HistorieAntwort extends Nachfassbar {
  documentId: number | null;
  fileName: string | null;
  mimeType: string | null;
  texterkennung: boolean;
  unlesbar: boolean;
  /** `null`, solange die Texterkennung läuft. */
  befund: {
    rechtsform: string | null;
    vertragVom: string | null;
    ersteEintragung: string | null;
    gegenstand: string | null;
    stammkapital: string | null;
    /** Frühere Rechtsform (Kurzform), nur wenn eindeutig aus dem Abdruck. */
    fruehereRechtsform?: string | null;
    historie: Historieneintrag[];
  } | null;
  vorschlaege: Feldvorschlag[];
  hinweise: string[];
}

/**
 * Die Geschichte der Gesellschaft aus dem chronologischen Abdruck (CD).
 *
 * Der aktuelle Abdruck zeigt den Stand von heute; der chronologische jede
 * Eintragung seit der ersten — Gründung, Rechtsform, Sitzverlegungen,
 * Kapitaländerungen, Formwechsel.
 */
export const registerhistorieAbrufen = (
  contactId: number,
  melden?: (laeuft: boolean) => void,
  /**
   * Den schon abgelegten Abdruck auswerten statt neu zu holen — kein
   * Portalabruf. Fehlt er (gelöscht), meldet das Backend 404, statt
   * ersatzweise neu abzurufen.
   */
  documentId?: number,
) =>
  bisFertig<HistorieAntwort>(
    () =>
      post(
        `firmendaten/registerhistorie/${contactId}`,
        documentId ? { documentId } : undefined,
      ),
    (documentId) => post(`firmendaten/registerhistorie/${contactId}`, { documentId }),
    melden,
  );

/** Größenklasse nach § 267 HGB. */
export interface GroessenklasseAntwort {
  merkmale: { bilanzsumme: number | null; umsatz: number | null; arbeitnehmer: number | null };
  befund: {
    klasse: "kleinst" | "klein" | "mittelgross" | "gross" | null;
    pruefungspflichtig: boolean | null;
    bekannt: number;
    begruendung: string[];
    hinweise: string[];
  };
  klasseText: string | null;
  vorschlaege: Feldvorschlag[];
  hinweise: string[];
}

/**
 * Größenklasse und Prüfungspflicht bestimmen. Beides steht NICHT im
 * Handelsregister — es ergibt sich aus drei Zahlen (§ 267 HGB).
 */
export const groessenklasseBestimmen = (
  contactId: number,
  merkmale: { bilanzsumme: number | null; umsatz: number | null; arbeitnehmer: number | null },
) => post<GroessenklasseAntwort>(`firmendaten/groessenklasse/${contactId}`, merkmale);

/** Ein Vorschlag aus dem Branchenkatalog. */
export interface Branchentreffer {
  id: number;
  ebene1: string;
  ebene2: string;
  ebene3: string;
  pfad: string;
  punkte: number;
  /** Die Wörter, die den Treffer getragen haben — die Begründung. */
  woerter: string[];
}

export interface BranchenvorschlagAntwort {
  /** Der Text, aus dem geschlossen wurde. */
  gegenstand: string;
  treffer: Branchentreffer[];
  vorhanden: { id: number; pfad: string } | null;
  hinweise: string[];
}

/**
 * Die Branche aus dem Unternehmensgegenstand vorschlagen — gerechnet gegen den
 * vorhandenen Katalog. Ohne `gegenstand` wird die Tätigkeitsbeschreibung des
 * Kontakts genommen.
 */
export const brancheVorschlagen = (contactId: number, gegenstand?: string | null) =>
  post<BranchenvorschlagAntwort>(`firmendaten/branche/${contactId}`, {
    gegenstand: gegenstand ?? null,
  });

/** Ergebnis des europäischen Bestätigungsverfahrens (MIAS/VIES). */
export interface UstIdAntwort {
  nummer: string;
  land: string;
  gueltig: boolean;
  geprueftAm: string | null;
  /** Name und Anschrift — Deutschland gibt sie nicht heraus. */
  name: string | null;
  anschrift: string | null;
  /** Stimmt der Name mit dem Kontakt überein? `null` = nicht prüfbar. */
  namensabgleich: boolean | null;
  vorschlaege: Feldvorschlag[];
  hinweise: string[];
}

/**
 * Die Umsatzsteuer-Identifikationsnummer bestätigen lassen.
 *
 * Sie steht nie im Handelsregister (Steuergeheimnis) — in KMVS kommt sie aus
 * dem Impressum. Ohne `nummer` wird die aus den Stammdaten geprüft.
 */
export const ustIdPruefen = (contactId: number, nummer?: string | null) =>
  post<UstIdAntwort>(`firmendaten/ustid/${contactId}`, { nummer: nummer ?? null });

/** Ein Treffer der Beteiligungsrecherche (Websuche in einem Verzeichnis). */
export interface RechercheEintrag {
  art: "firma" | "person";
  name: string;
  nachname?: string | null;
  vorname?: string | null;
  ort?: string | null;
  /** „HRB 21230" — mit Gericht zusammen der Schlüssel zum Registerportal. */
  registernummer?: string | null;
  registergericht?: string | null;
  url: string;
  snippet?: string | null;
  /** Schon als Kontakt vorhanden — dann wird nicht doppelt angelegt. */
  kontaktId?: number | null;
  kontaktName?: string | null;
  /** Nummer UND Gericht bekannt: direkt im Register abrufbar. */
  abrufbar: boolean;
  /** In welcher Runde der Treffer auftauchte. 0 = die Suche nach dem Ausgangsnamen. */
  tiefe: number;
}

/**
 * Eine Runde der Recherche: wonach gesucht wurde, was dabei herauskam und
 * welche Runden daraus entstanden sind.
 */
export interface RechercheKnoten {
  gesucht: { name: string; ort: string | null };
  anfrage: string;
  tiefe: number;
  treffer: RechercheEintrag[];
  kinder: RechercheKnoten[];
}

export interface RechercheErgebnis {
  /** `laeuft`: Der Auftrag arbeitet noch — erneut fragen, ohne neue Kosten. */
  status: "fertig" | "laeuft";
  /** Die Anfrage der ersten Runde — sichtbar, damit sie beurteilbar bleibt. */
  anfrage: string;
  gesucht: { name: string; ort: string | null };
  /** Alle Treffer aller Runden, dublettenfrei. */
  treffer: RechercheEintrag[];
  /** Dieselben Treffer, nach Runden geordnet. */
  wurzel: RechercheKnoten;
  /** Namen, die das Anfragen-Budget nicht mehr hergab. */
  offen: Array<{ name: string; ort: string | null; tiefe: number }>;
  /** Wie viele Suchanfragen dieser Auftrag verbraucht hat. */
  anfragen: number;
  /** Der Lauf endete am geteilten Tagesdeckel, nicht an seiner eigenen Grenze. */
  budgetErschoepft: boolean;
  budget: { verbrauchtHeute: number; tagesdeckel: number };
  hinweise: string[];
}

/**
 * So lange nachfassen, bis der Auftrag durch ist.
 *
 * Ein Auftrag stellt bis zu acht Suchanfragen, jede dauert 8 bis 29 Sekunden —
 * zusammen mehr als die 30 Sekunden, nach denen der vorgelagerte Proxy auflegt.
 * Das Backend antwortet deshalb rechtzeitig mit `laeuft` und rechnet im
 * Hintergrund weiter. Jeder Aufruf wartet dort selbst rund 15 Sekunden auf das
 * Ergebnis; diese Schleife läuft also nicht heiß und stellt keine zweite Suche
 * an — der laufende Auftrag wird weiterverwendet.
 */
const RECHERCHE_GRENZE_MS = 300_000;

async function rechercheBisFertig(
  abrufen: () => Promise<RechercheErgebnis>,
): Promise<RechercheErgebnis> {
  let antwort = await abrufen();
  const bis = Date.now() + RECHERCHE_GRENZE_MS;
  while (antwort.status === "laeuft") {
    if (Date.now() >= bis) {
      throw new Error(
        "Die Recherche dauert ungewöhnlich lange. Es wurde nichts angelegt — " +
          "bitte gleich noch einmal suchen.",
      );
    }
    antwort = await abrufen();
  }
  return antwort;
}

/**
 * Weitere Beteiligungen suchen — die Frage, die ein Registerauszug nicht
 * beantwortet: An welchen ANDEREN Gesellschaften ist dieser Mensch beteiligt?
 *
 * Aus den ersten Treffern wird automatisch weitergesucht (zwei Ebenen tief,
 * höchstens acht Suchanfragen je Auftrag).
 */
export const beteiligungenRecherchieren = (contactId: number) =>
  rechercheBisFertig(() => post<RechercheErgebnis>(`firmendaten/recherche/${contactId}`));

/** Dasselbe für einen Namen, den es als Kontakt noch nicht gibt. */
export const beteiligungenRecherchierenFuerNamen = (name: string, ort?: string | null) =>
  rechercheBisFertig(() =>
    post<RechercheErgebnis>("firmendaten/recherche", { name, ort: ort ?? null }),
  );

export interface GesellschaftAnlageErgebnis {
  kontaktId: number;
  /** War die Gesellschaft neu, oder gab es sie schon? */
  neu: boolean;
  angelegt: Array<{ kontaktId: number; name: string; rolle: string; neu: boolean }>;
  fehler: Array<{ name: string; grund: string }>;
}

/**
 * Eine in der Recherche gefundene und gegen das Register bestätigte
 * Gesellschaft anlegen — mit den Personen, die der Anwender ausgewählt hat.
 */
export const gesellschaftAusRegisterAnlegen = (angaben: {
  name: string;
  registerNummer: string;
  registergericht: string;
  kontaktId?: number | null;
  eintraege?: UebernahmeEintrag[];
}) =>
  post<GesellschaftAnlageErgebnis>("firmendaten/register-beteiligte/gesellschaft", angaben);

/** Beschriftungen der Befund-Felder für die Übernahme-Liste. */
export const FELD_LABELS: Record<keyof BefundFelder, string> = {
  name: "Firmenname (laut Impressum)",
  website: "Internet",
  phone: "Telefon-Nr.",
  fax: "Fax-Nr.",
  email: "E-Mail",
  handelsregisterNummer: "Handelsregister-Nr.",
  registergericht: "Registergericht",
  umsatzsteuernummer: "USt-IdNr.",
  steuernummer: "Steuernummer",
  street: "Straße und Hausnummer",
  postalcode: "Postleitzahl",
  city: "Ort",
  rechtsform: "Rechtsform",
  vertretung: "Vertreten durch",
};

/**
 * Reihenfolge der Anzeige. „Vertreten durch" und „Rechtsform" stehen am Ende:
 * die Rechtsform erkennt die Maske schon selbst aus dem Namen, und der
 * Geschäftsführer ist kein Feld der Firmenmaske, sondern eine Person — er wird
 * angezeigt, aber nicht zum Übernehmen angeboten.
 */
export const FELD_REIHENFOLGE: Array<keyof BefundFelder> = [
  "name",
  "website",
  "phone",
  "fax",
  "email",
  "street",
  "postalcode",
  "city",
  "handelsregisterNummer",
  "registergericht",
  "umsatzsteuernummer",
  "steuernummer",
];

/** Ein gefundenes Profilbild aus einem Karrierenetzwerk. */
export interface Bildvorschlag {
  netzwerk: "linkedin" | "xing";
  netzwerkName: string;
  /** Googles Beschreibung, meist „Name – Position | Netzwerk". */
  beschreibung: string;
  /** Das Bild als data:-URL — direkt anzeigbar. */
  bild: string;
  /** 0…1, sortiert vor. Entscheidet nichts: Auswählen muss ein Mensch. */
  guete: number;
  begruendung: string[];
}

export interface ProfilbildAntwort {
  /** `laeuft`: Die Suche läuft noch — erneut fragen, ohne neue Kosten. */
  status: "fertig" | "laeuft";
  person: { name: string; firma: string | null };
  vorschlaege: Bildvorschlag[];
  hinweise: string[];
}

/**
 * Profilbilder zu einer Person in LinkedIn und Xing suchen.
 *
 * Gesucht wird über die Google-Bildersuche, weil die Profilseiten selbst mit
 * dem eingerichteten Zugang nicht abrufbar sind. Das Bild kommt dort
 * eingebettet mit — es muss also nichts nachgeladen werden, und es kann auch
 * nichts ablaufen.
 */
export const profilbildSuchen = async (
  contactId: number,
  melden?: (laeuft: boolean) => void,
): Promise<ProfilbildAntwort> => {
  // Nachfassen, bis die Suche durch ist: Eine Anfrage bei Bright Data dauert 8
  // bis 29 Sekunden, ein vorgelagerter Proxy legt nach 30 auf. Das Backend
  // antwortet deshalb sofort mit `laeuft` und führt den Lauf im Hintergrund
  // weiter — jedes Nachfragen holt denselben Lauf ab, keine neue Abfrage.
  const bis = Date.now() + 180_000;
  let antwort = await post<ProfilbildAntwort>(`firmendaten/profilbild/${contactId}`);
  try {
    while (antwort.status === "laeuft") {
      melden?.(true);
      if (Date.now() >= bis) {
        throw new Error(
          "Die Suche in den Netzwerken dauert ungewöhnlich lange — bitte gleich " +
            "noch einmal versuchen.",
        );
      }
      antwort = await post<ProfilbildAntwort>(`firmendaten/profilbild/${contactId}`);
    }
  } finally {
    melden?.(false);
  }
  return antwort;
};

/** Das ausgewählte Bild als Kontaktfoto übernehmen. */
export const profilbildUebernehmen = (contactId: number, bild: string) =>
  post<{ photo_url: string }>(`firmendaten/profilbild/${contactId}/uebernehmen`, { bild });

// ── Logo-Vorschlag (ersetzt das automatisch geladene Favicon) ───────────────

export interface LogoVorschlag {
  /** Das Bild als `data:`-URL (PNG/JPEG/WebP/GIF, vom Server geprüft) — oder `null`. */
  logo: string | null;
  /** Woher das Bild kam. */
  quelle: string | null;
  /** Warum es keinen Vorschlag gibt. */
  hinweis: string | null;
}

/**
 * Ein Logo von der Website der Firma vorschlagen lassen. Der SERVER holt das
 * Bild (mit SSRF-Schutz) und prüft es an den Bytes — der Browser lädt nichts
 * von der Kundendomain. Gespeichert wird nichts.
 */
export const logoVorschlagHolen = (url: string) =>
  post<LogoVorschlag>("firmendaten/logo/vorschlag", { url });

/**
 * Das bestätigte Logo an einer bestehenden Firma speichern. Ein vorhandenes
 * Bild ersetzt der Server nur mit `ersetzen: true` (sonst HTTP 409).
 */
export const logoUebernehmen = (contactId: number, bild: string, ersetzen: boolean) =>
  post<{ photo_url: string; version: number | null }>(
    `firmendaten/logo/${contactId}/uebernehmen`,
    { bild, ersetzen },
  );
